<script lang="ts" setup>
import { Badge, BadgeDot } from '~ds/components/ui/badge';
import { Icon } from '~ds/components/ui/icon';
import { Spotlight } from '~ds/components/ui/spotlight';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

// The heading block the landing sections share. An illustration turns it into a two-column row
// with the copy ranged left; without one the copy is a centred column. The title is a slot so the
// renderer owns the copy: a sentence with an emphasised run stays one continuous string in the
// locale file, rendered through the caller's own `<i18n-t>`.
interface Props {
  description?: string;
  spotlightPosition?: 'top-right' | 'bottom-left';
  rounded?: boolean;
  illustration?: string;
  label?: string;
  items?: string[];
  surface?: 'card' | 'bare';
}

const props = withDefaults(defineProps<Props>(), {
  description: undefined,
  spotlightPosition: undefined,
  rounded: true,
  illustration: undefined,
  label: undefined,
  items: () => [],
  surface: 'card',
});

const layout = computed(() => (props.illustration ? 'split' : 'centred'));

const blockVariants = cva('relative w-full', {
  variants: {
    surface: {
      card: 'overflow-hidden bg-card py-20 px-10',
      bare: '',
    },
    rounded: {
      true: 'rounded-card-lg',
      false: '',
    },
    layout: {
      centred: 'text-center',
      split: 'text-left',
    },
  },
  defaultVariants: {
    surface: 'card',
    rounded: true,
    layout: 'centred',
  },
});

const contentVariants = cva('relative mx-auto', {
  variants: {
    layout: {
      centred: 'flex max-w-2xl flex-col',
      split: 'grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-2',
    },
  },
  defaultVariants: {
    layout: 'centred',
  },
});

const copyVariants = cva('flex flex-col gap-12', {
  variants: {
    layout: {
      centred: 'items-center',
      split: 'items-start',
    },
  },
  defaultVariants: {
    layout: 'centred',
  },
});
</script>

<template>
  <section>
    <div
      :class="twMerge(blockVariants({ rounded, layout, surface }))"
      data-testid="schools-landing-section-block-surface"
    >
      <Spotlight
        v-if="spotlightPosition"
        color="lime"
        size="lg"
        :position="spotlightPosition"
      />

      <div :class="twMerge(contentVariants({ layout }))">
        <div :class="twMerge(copyVariants({ layout }))">
          <div class="flex flex-col gap-5">
            <Badge
              v-if="label"
              tone="lime"
              rounded
              class="uppercase"
              data-testid="schools-landing-section-block-label"
            >
              <BadgeDot />
              {{ label }}
            </Badge>

            <Heading
              as="h2"
              size="2xl"
              data-testid="schools-landing-section-block-title"
            >
              <slot name="title" />
            </Heading>

            <Text
              v-if="description"
              size="lg"
              weight="medium"
              data-testid="schools-landing-section-block-description"
            >
              {{ description }}
            </Text>
          </div>

          <ul
            v-if="items.length"
            class="flex flex-col gap-4"
            data-testid="schools-landing-section-block-items"
          >
            <li
              v-for="item in items"
              :key="item"
              class="flex items-center gap-4"
            >
              <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary">
                <Icon :name="ICONS.tick" size="sm" />
              </span>
              <Text size="lg" weight="medium">
                {{ item }}
              </Text>
            </li>
          </ul>
        </div>

        <div
          v-if="illustration"
          class="relative aspect-5/4 w-full max-w-180 justify-self-center"
          data-testid="schools-landing-section-block-illustration"
        >
          <NuxtPicture
            format="avif,webp"
            :src="illustration"
            sizes="100vw lg:720px"
            :img-attrs="{ alt: '', class: 'absolute inset-0 size-full object-contain' }"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
</template>
